import {useCallback} from 'react';

import {useApolloClient} from '@apollo/react-hooks';

import type {MarketId, ProductId} from 'nutshell-core/types';

import type {
    GetProductPickerProducts,
    GetProductPickerProductsVariables,
} from 'nutshell-graphql-types';

import type {ProductMenuPage} from '../../hooks/use-paginated-product-menu';

import type {ProductPickerProduct} from '../helpers/product-picker-product';
import GET_PRODUCT_PICKER_PRODUCTS from '../queries/get-product-picker-products.graphql';

const PRODUCT_PAGE_SIZE = 50;

interface Options {
    marketId?: MarketId;
    omittedProductIds?: ProductId[];
}

export function useGetProductPickerProducts({marketId, omittedProductIds = []}: Options): {
    getProductPickerProducts: (
        search: string | null,
        after?: string | null
    ) => Promise<ProductMenuPage<ProductPickerProduct>>;
} {
    const client = useApolloClient();

    const getProductPickerProducts = useCallback(
        async (
            search: string | null,
            after?: string | null
        ): Promise<ProductMenuPage<ProductPickerProduct>> => {
            const {data} = await client.query<
                GetProductPickerProducts,
                GetProductPickerProductsVariables
            >({
                query: GET_PRODUCT_PICKER_PRODUCTS,
                variables: {
                    search,
                    first: PRODUCT_PAGE_SIZE,
                    ...(after ? {after} : {}),
                    ...(marketId ? {marketId} : {}),
                    ...(omittedProductIds.length ? {omittedIds: omittedProductIds} : {}),
                },
                fetchPolicy: 'no-cache',
            });

            return {
                items: data.productPickerProducts.edges.map(({node, marketPrice, isAvailable}) => {
                    const prices: ProductPickerProduct['prices'] = {};
                    if (marketId && marketPrice) {
                        prices[marketId] = {
                            amount: String(marketPrice.amount),
                            isAvailable: Boolean(isAvailable),
                        };
                    }

                    return {
                        id: node.id,
                        name: node.name,
                        sku: node.sku,
                        category: node.category,
                        priceMarketId: marketId ?? null,
                        prices,
                    };
                }),
                endCursor: data.productPickerProducts.pageInfo.endCursor,
                hasNextPage: data.productPickerProducts.pageInfo.hasNextPage,
            };
        },
        [client, marketId, omittedProductIds]
    );

    return {getProductPickerProducts};
}
